import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, message, type Body, type Client, type ClientDetail, type Person } from "../api";
import { BodyEditor, HonorificInput, PersonEditor, PronounSelect } from "./Roster";
import { DirectoryImport, MembersImport } from "./Directory";
import { Modal } from "../Modal";

export function Clients({ isAdmin }: { isAdmin: boolean }) {
  const [rows, setRows] = useState<Client[] | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [timezone, setTimezone] = useState("America/Chicago");
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try { setRows(await api.get<Client[]>("/api/clients")); }
    catch (err) { setError(message(err)); setRows([]); }
  }
  useEffect(() => { void load(); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const made = await api.post<Client>("/api/clients", { name, slug, timezone });
      setName(""); setSlug(""); setOpen(false);
      // Straight to its page: that is where the rest of the setup is done.
      navigate(`/clients/${made.id}`);
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="pagehead">
        <h1>Clients</h1>
        {isAdmin && (
          <button className="btn btn--inline" onClick={() => setOpen((v) => !v)}>
            {open ? "Cancel" : "New client"}
          </button>
        )}
      </div>

      {error && <div className="alert" role="alert">{error}</div>}

      {open && (
        <form className="card card--form" onSubmit={submit}>
          <div className="field">
            <label htmlFor="cname">Name</label>
            <input
              id="cname" required value={name}
              placeholder="City of Brooklyn Center"
              onChange={(e) => {
                setName(e.target.value);
                // Derive the slug until the user edits it themselves.
                setSlug((s) =>
                  s === "" || s === toSlug(name)
                    ? toSlug(e.target.value) : s);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="cslug">Short name</label>
            <input
              id="cslug" required value={slug} pattern="[a-z0-9-]+"
              onChange={(e) => setSlug(e.target.value)}
            />
            <p className="hint">Lowercase letters, numbers and hyphens.</p>
          </div>
          <div className="field">
            <label htmlFor="ctz">Time zone</label>
            <TimezoneSelect id="ctz" value={timezone} onChange={setTimezone} />
            <p className="hint">Meeting dates and file names are worked out in the client's own time.</p>
          </div>
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create client"}
          </button>
        </form>
      )}

      {rows === null ? <p className="muted">Loading…</p>
        : rows.length === 0 ? (
          <div className="card empty">
            <p><strong>No clients yet.</strong></p>
            <p className="muted">
              {isAdmin ? "Create one to get started."
                : "You haven't been given access to any clients yet."}
            </p>
          </div>
        ) : (
          <div className="grid">
            {rows.map((c) => (
              <Link key={c.id} to={`/clients/${c.id}`} className="card card--link">
                <h2>{c.name}</h2>
                <p className="muted mono small">{c.slug} · {c.timezone}</p>
              </Link>
            ))}
          </div>
        )}
    </>
  );
}

const ZONES: [string, string][] = [
  ["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona (no daylight time)"], ["America/Los_Angeles", "Pacific"],
  ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"],
];

function TimezoneSelect(props: { id: string; value: string; onChange: (v: string) => void }) {
  const known = ZONES.some(([z]) => z === props.value);
  return (
    <select id={props.id} value={props.value} onChange={(e) => props.onChange(e.target.value)}>
      {!known && <option value={props.value}>{props.value}</option>}
      {ZONES.map(([z, label]) => <option key={z} value={z}>{label} — {z}</option>)}
    </select>
  );
}

/** Name and time zone, the two things about a client set when it was made. */
function ClientSettings(props: { client: ClientDetail; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(props.client.name);
  const [timezone, setTimezone] = useState(props.client.timezone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.patch(`/api/clients/${props.client.id}`, { name, timezone }); props.onDone(); }
    catch (err) { setError(message(err)); setBusy(false); }
  }

  return (
    <form className="card card--form roster-editor" onSubmit={submit}>
      <h2>Client settings</h2>
      {error && <div className="alert" role="alert">{error}</div>}
      <div className="field">
        <label htmlFor="csname">Name</label>
        <input id="csname" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="cstz">Time zone</label>
        <TimezoneSelect id="cstz" value={timezone} onChange={setTimezone} />
      </div>
      <div className="actions">
        <button className="btn btn--inline" type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        <button className="linkbtn" type="button" onClick={props.onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * A new kind of meeting. Only what is needed to create it; the template and
 * style rules are added on its own page, which opens next.
 */
function MeetingTypeCreator(props: { clientId: string; bodies: Body[]; onCancel: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [bodyIds, setBodyIds] = useState<string[]>(props.bodies.length === 1 ? [props.bodies[0]!.id] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (bodyIds.length === 0) { setError("Choose at least one body: it sets the titles the minutes use."); return; }
    setBusy(true); setError(null);
    try {
      const made = await api.post<{ id: string }>(`/api/clients/${props.clientId}/meeting-types`, { name, code, bodyIds });
      navigate(`/meeting-types/${made.id}`);
    } catch (err) { setError(message(err)); setBusy(false); }
  }
  const toggle = (id: string) => setBodyIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <form className="card card--form roster-editor" onSubmit={submit}>
      <h2>Add a meeting type</h2>
      {error && <div className="alert" role="alert">{error}</div>}
      <div className="field-row">
        <div className="field">
          <label htmlFor="ntname">Name</label>
          <input id="ntname" required value={name} placeholder="Regular Session" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ntcode">Code</label>
          <input id="ntcode" required value={code} className="mono" placeholder="CC" maxLength={12}
                 pattern="[A-Za-z0-9.]+" onChange={(e) => setCode(e.target.value)} />
        </div>
      </div>
      <p className="hint">The name as it is said in the meeting; the code is a short tag used in file names.</p>
      <fieldset className="field checks">
        <legend>Bodies meeting</legend>
        {props.bodies.map((b) => (
          <label key={b.id} className="check">
            <input type="checkbox" checked={bodyIds.includes(b.id)} onChange={() => toggle(b.id)} />
            <span>{b.name} <span className="muted">({b.abbrev})</span></span>
          </label>
        ))}
        <p className="hint">Whoever is seated on these bodies is addressed by their title there. A joint meeting has two.</p>
      </fieldset>
      <div className="actions">
        <button className="btn btn--inline" type="submit" disabled={busy}>
          {busy ? "Adding…" : "Add, then upload its template"}
        </button>
        <button className="linkbtn" type="button" onClick={props.onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * What a client still needs before its minutes come out right, in the order
 * to do it. Shown until every step is done.
 */
function SetupGuide({ c }: { c: ClientDetail }) {
  const types = c.meetingTypes.filter((t) => t.active);
  const people = c.people.filter((p) => p.active);
  const steps: { done: boolean; title: string; detail: string }[] = [
    { done: c.bodies.length > 0, title: "Add each body",
      detail: "City Council, a commission, an authority — with the titles its presiding officer and members go by." },
    { done: types.length > 0, title: "Add each kind of meeting",
      detail: "Regular Session, Work Session and so on, each tied to the body that meets." },
    { done: types.length > 0 && types.every((t) => t.hasFrame), title: "Upload a template for each kind of meeting",
      detail: "The Word file the minutes are written into. Open a meeting type to upload it." },
    { done: types.length > 0 && types.every((t) => t.promptNotesChars > 0), title: "Paste the style rules for each kind of meeting",
      detail: "The tip sheet: motion wording, how votes are recorded, how people are named." },
    { done: people.some((p) => p.seats.length > 0), title: "Add the officials and seat them",
      detail: "Import them from each body's page on the city site (Bodies, Import members), or add a person and give them their seat — that sets Mayor, Councilmember, Chair." },
    { done: people.some((p) => p.kind === "staff"), title: "Add the staff",
      detail: "One at a time, or all at once from the city's staff directory page." },
    { done: Boolean(c.notes?.trim()), title: "Add client-wide notes and acronyms",
      detail: "Optional. Anything true of every meeting, and lines like \"EDA = Economic Development Authority\"." },
  ];
  const left = steps.filter((s) => !s.done).length;
  if (left === 0) return null;
  return (
    <section className="card setup">
      <h2>Setting up this client</h2>
      <p className="muted small">
        {left === steps.length ? "Nothing is set up yet. " : `${steps.length - left} of ${steps.length} done. `}
        Work down the list; each step is on this page.
      </p>
      <ol className="setup__steps">
        {steps.map((s) => (
          <li key={s.title} className={s.done ? "setup__step setup__step--done" : "setup__step"}>
            <span className="setup__mark" aria-hidden="true">{s.done ? "✓" : ""}</span>
            <span><strong>{s.title}</strong>{s.done && <span className="visually-hidden"> (done)</span>}
              <span className="muted small"> — {s.detail}</span></span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function toSlug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Notes the drafter is given with every meeting for this client, and its
 * glossary: lines like "VEAP = Volunteers Enlisted to Assist People".
 */
function ClientNotes(props: { clientId: string; notes: string | null; isAdmin: boolean; onSaved: () => void }) {
  const [text, setText] = useState(props.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setText(props.notes ?? ""); }, [props.notes]);
  const dirty = text !== (props.notes ?? "");

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.patch(`/api/clients/${props.clientId}`, { notes: text.trim() ? text : null });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      props.onSaved();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <h2>Notes and glossary</h2>
      <p className="muted small">
        Given to the drafter with every meeting. Put each acronym on its own line —{" "}
        <span className="mono">VEAP = Volunteers Enlisted to Assist People</span> — and the minutes will spell it out
        the first time it appears, use the acronym after that, and the transcription will listen for it. The
        bodies above (EDA, HRA…) are already known.
      </p>
      {error && <div className="alert" role="alert">{error}</div>}
      {props.isAdmin ? (
        <>
          <textarea className="notes-box" rows={6} value={text} onChange={(e) => setText(e.target.value)}
                    aria-label="Client notes and glossary" />
          <div className="actions">
            <button className="btn btn--inline btn--small" disabled={busy || !dirty} onClick={() => void save()}>
              {busy ? "Saving…" : "Save notes"}
            </button>
            {saved && <span className="ok small">Saved</span>}
          </div>
        </>
      ) : <pre className="notes-box notes-box--read">{props.notes ?? "None."}</pre>}
    </section>
  );
}

export function ClientDetailPage({ isAdmin }: { isAdmin: boolean }) {
  const { id } = useParams<{ id: string }>();
  const [c, setC] = useState<ClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [honorifics, setHonorifics] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  /** The person or body being added (null inside) or edited. */
  const [editing, setEditing] = useState<{ person: Person | null } | null>(null);
  const [bodyEditing, setBodyEditing] = useState<{ body: Body | null } | null>(null);
  const [seating, setSeating] = useState<Body | null>(null);
  const [importing, setImporting] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [settings, setSettings] = useState(false);

  async function setHonorific(personId: string, value: string) {
    setHonorifics((h) => ({ ...h, [personId]: value }));
    try {
      await api.patch(`/api/people/${personId}`, { honorificPrefix: value || null });
      setSaved(personId);
      setTimeout(() => setSaved((s) => (s === personId ? null : s)), 1500);
    } catch (err) { setError(message(err)); }
  }

  async function setPronoun(p: Person, value: "he" | "she" | null) {
    try {
      await api.patch(`/api/people/${p.id}`, { pronoun: value });
      setSaved(p.id);
      setTimeout(() => setSaved((s) => (s === p.id ? null : s)), 1500);
      reload();
    } catch (err) { setError(message(err)); }
  }

  const reload = useCallback(() => {
    if (!id) return;
    api.get<ClientDetail>(`/api/clients/${id}`)
      .then((d) => { setC(d); setHonorifics({}); })
      .catch((e) => setError(message(e)));
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  /** The hard delete for a records request. */
  async function forgetVoice(p: Person) {
    const ok = window.confirm(
      `Permanently delete the voiceprint for ${p.firstName} ${p.lastName}?\n\n` +
      "This cannot be undone. Their name stays on past transcripts; future recordings " +
      "will no longer recognise their voice until it is enrolled again.");
    if (!ok) return;
    try { await api.del(`/api/people/${p.id}/voiceprint`); reload(); }
    catch (err) { setError(message(err)); }
  }

  const pronounCell = (p: Person) => (isAdmin
    ? <PronounSelect ariaLabel={`Pronoun for ${p.firstName} ${p.lastName}`} value={p.pronoun}
                     honorific={p.honorificPrefix} onChange={(v) => void setPronoun(p, v)} />
    : <span className="small">{p.pronoun ?? <span className="muted">—</span>}</span>);

  const voiceCell = (p: Person) => {
    const v = p.voiceprint;
    if (!v) return <span className="muted small">—</span>;
    const label = v.status === "ready" ? "On file" : v.status === "pending" ? "Building…" : "Failed";
    return (
      <span className="small">
        <span className={v.status === "failed" ? "warn" : ""}>{label}</span>
        {isAdmin && (
          <>
            {" · "}
            <button className="linkbtn" onClick={() => void forgetVoice(p)}>Delete</button>
          </>
        )}
      </span>
    );
  };

  if (!c) return error ? <div className="alert" role="alert">{error}</div> : <p className="muted">Loading…</p>;

  const current = c.people.filter((p) => p.active);
  const officials = current.filter((p) => p.seats.length > 0);
  const staff = current.filter((p) => p.kind === "staff");
  const others = current.filter((p) => p.seats.length === 0 && p.kind !== "staff");
  const former = c.people.filter((p) => !p.active);

  const done = () => { setEditing(null); reload(); };
  const editButton = (p: Person) => isAdmin && (
    <button className="linkbtn small" onClick={() => setEditing({ person: p })}>Edit</button>
  );

  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb"><Link to="/clients">Clients</Link></nav>
      <div className="pagehead">
        <div>
          <h1>{c.name}</h1>
          <p className="muted mono small">
            {c.slug} · {c.timezone}
            {isAdmin && <> · <button className="linkbtn small" onClick={() => setSettings(true)}>Edit</button></>}
          </p>
        </div>
        <Link className="linkbtn" to="/clients">← All clients</Link>
      </div>

      {error && <div className="alert" role="alert">{error}</div>}

      {settings && (
        <Modal label="Client settings" onClose={() => setSettings(false)}>
          <ClientSettings client={c} onDone={() => { setSettings(false); reload(); }} onCancel={() => setSettings(false)} />
        </Modal>
      )}

      {isAdmin && <SetupGuide c={c} />}

      <ClientNotes clientId={c.id} notes={c.notes} isAdmin={isAdmin} onSaved={reload} />

      <section className="card">
        <div className="cardhead">
          <h2>Meeting types</h2>
          {isAdmin && (
            <button className="btn btn--inline btn--ghost" disabled={c.bodies.length === 0}
                    title={c.bodies.length === 0 ? "Add a body first" : undefined}
                    onClick={() => setAddingType(true)}>
              Add a meeting type
            </button>
          )}
        </div>
        <p className="muted small">
          Each kind of meeting carries its own minutes template and style rules. A recording
          can contain several. {isAdmin && "Open one to upload its template or edit its rules."}
          {isAdmin && c.bodies.length === 0 && " Add a body below first: a meeting type belongs to the body that meets."}
        </p>
        {addingType && (
          <Modal label="Add a meeting type" onClose={() => setAddingType(false)}>
            <MeetingTypeCreator clientId={c.id} bodies={c.bodies} onCancel={() => setAddingType(false)} />
          </Modal>
        )}
        {c.meetingTypes.length === 0 && <p className="muted">None yet.</p>}
        <table className="table" hidden={c.meetingTypes.length === 0}>
          <thead>
            <tr><th>Code</th><th>Name</th><th>Bodies</th><th>Template</th><th>Style rules</th></tr>
          </thead>
          <tbody>
            {c.meetingTypes.map((t) => (
              <tr key={t.id}>
                <td><span className="pill">{t.code}</span></td>
                <td>
                  {isAdmin ? <Link to={`/meeting-types/${t.id}`}>{t.name}</Link> : t.name}
                  {!t.active && <span className="muted small"> (not in use)</span>}
                </td>
                <td className="muted small">{t.bodies.join(", ")}</td>
                <td>
                  {t.hasFrame
                    ? `${t.sections} sections`
                    : <span className="warn">none</span>}
                </td>
                <td>
                  {t.promptNotesChars > 0
                    ? `${t.promptNotesChars.toLocaleString()} chars`
                    : <span className="warn">none</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="cardhead">
          <h2>Bodies</h2>
          {isAdmin && (
            <button className="btn btn--inline btn--ghost" onClick={() => setBodyEditing({ body: null })}>
              Add a body
            </button>
          )}
        </div>
        {bodyEditing && (
          <Modal label={bodyEditing.body ? "Edit body" : "Add a body"} onClose={() => setBodyEditing(null)}>
            <BodyEditor key={bodyEditing.body?.id ?? "new"} clientId={c.id} body={bodyEditing.body}
                        onDone={() => { setBodyEditing(null); reload(); }} onCancel={() => setBodyEditing(null)} />
          </Modal>
        )}
        {seating && (
          <Modal label={`Import members of the ${seating.name}`} onClose={() => setSeating(null)}>
            <MembersImport body={seating} onDone={() => { setSeating(null); reload(); }} onCancel={() => setSeating(null)} />
          </Modal>
        )}
        <table className="table">
          <thead><tr><th>Abbrev</th><th>Name</th><th>Presiding</th><th>Members</th><th></th></tr></thead>
          <tbody>
            {c.bodies.map((b) => (
              <tr key={b.id}>
                <td><span className="pill">{b.abbrev}</span></td>
                <td>{b.name}</td>
                <td>{b.presidingTitle}{b.proTemTitle ? ` (${b.proTemTitle})` : ""}</td>
                <td>{b.memberTitle}</td>
                <td className="nowrap">
                  {isAdmin && <button className="linkbtn small" onClick={() => setBodyEditing({ body: b })}>Edit</button>}
                  {isAdmin && <button className="linkbtn small" onClick={() => setSeating(b)}>Import members</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="pagehead pagehead--section">
        <h2>People</h2>
        {isAdmin && (
          <button className="btn btn--inline" onClick={() => setEditing({ person: null })}>Add a person</button>
        )}
      </div>
      {editing && (
        <Modal label={editing.person ? "Edit person" : "Add a person"} onClose={() => setEditing(null)}>
          <PersonEditor key={editing.person?.id ?? "new"} clientId={c.id} person={editing.person} bodies={c.bodies}
                        onDone={done} onCancel={() => setEditing(null)} />
        </Modal>
      )}

      <section className="card">
        <h2>Seated members</h2>
        <p className="muted small">
          How the minutes will address each person — the same person can hold a
          different title on each body. The pronoun lets the minutes say "He stated…" or
          "She asked…" after naming someone; with none set, the name is repeated every
          time. A voice on file lets new recordings suggest
          them by voice; it is enrolled from a meeting page, and deleting it here is
          permanent.
        </p>
        <table className="table">
          <thead><tr><th>Name</th><th>Titles</th><th>Pronoun</th><th>Voice</th><th></th></tr></thead>
          <tbody>
            {officials.map((p) => (
              <tr key={p.id}>
                <td>{p.firstName} {p.lastName}</td>
                <td>
                  {p.seats.map((s) => (
                    <span key={s.body} className="pill pill--quiet">
                      {s.body}: {s.title}
                    </span>
                  ))}
                </td>
                <td>{pronounCell(p)}</td>
                <td>{voiceCell(p)}</td>
                <td>{editButton(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="cardhead">
          <h2>Staff <span className="muted">({staff.length})</span></h2>
          {isAdmin && !importing && (
            <button className="btn btn--inline btn--ghost" onClick={() => setImporting(true)}>
              Import from staff directory
            </button>
          )}
        </div>
        {importing && (
          <DirectoryImport clientId={c.id} initialUrl={c.staffDirectoryUrl}
                           onDone={() => { setImporting(false); reload(); }} onCancel={() => setImporting(false)} />
        )}
        <p className="muted small">
          Minutes name staff by title on first mention, then by what's set here and the
          surname: an honorific ("Ms. Sabin") or a title ("Chief Sabin", "Director
          Sabin"). It is never guessed from a name — until it's set, the minutes leave a
          highlighted blank for the clerk.
        </p>
        <table className="table">
          <thead><tr><th>Name</th><th>Title</th><th>Later mentions</th><th>Pronoun</th><th>Voice</th><th></th></tr></thead>
          <tbody>
            {staff.map((p) => (
              <tr key={p.id}>
                <td>{p.firstName} {p.lastName}</td>
                <td className="small">{p.staffTitle}</td>
                <td>
                  {isAdmin ? (
                    <HonorificInput
                      ariaLabel={`Honorific or title for ${p.firstName} ${p.lastName}`}
                      value={honorifics[p.id] ?? p.honorificPrefix ?? ""}
                      onChange={(v) => setHonorifics((h) => ({ ...h, [p.id]: v }))}
                      onCommit={(v) => { if (v !== (p.honorificPrefix ?? "")) void setHonorific(p.id, v); }}
                    />
                  ) : (p.honorificPrefix ?? <span className="muted">Not set</span>)}
                  {saved === p.id && <span className="ok small"> ✓</span>}
                </td>
                <td>{pronounCell(p)}</td>
                <td>{voiceCell(p)}</td>
                <td>{editButton(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {others.length > 0 && (
        <section className="card">
          <h2>Others <span className="muted">({others.length})</span></h2>
          <p className="muted small">On the roster without a seat or a staff role; offered when naming speakers.</p>
          <table className="table">
            <tbody>
              {others.map((p) => (
                <tr key={p.id}>
                  <td>{p.firstName} {p.lastName}</td>
                  <td className="small">{p.staffTitle ?? <span className="muted">{p.kind}</span>}</td>
                  <td>{editButton(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {former.length > 0 && (
        <section className="card">
          <h2>Former <span className="muted">({former.length})</span></h2>
          <p className="muted small">
            No longer offered when naming speakers, but still named in the meetings they took part in.
          </p>
          <table className="table">
            <tbody>
              {former.map((p) => (
                <tr key={p.id} className="row--off">
                  <td>{p.firstName} {p.lastName}</td>
                  <td className="small">{p.seats.map((s) => `${s.body}: ${s.title}`).join(" · ") || p.staffTitle}</td>
                  <td>{voiceCell(p)}</td>
                  <td>{editButton(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
